/**
 * Shared input validation helpers.
 *
 * The app historically used a loose `^[^\s@]+@[^\s@]+\.[^\s@]+$` pattern in a
 * handful of places, which still accepts malformed addresses such as
 * `-@-.com` and `customer@test..com`. This validator tightens that up: it
 * requires a non-empty local part, valid domain labels (no leading/trailing
 * hyphens, no empty labels from consecutive dots) and a real TLD of 2+ letters.
 */
const EMAIL_REGEX =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;

/**
 * Maximum allowed email length. RFC 5321 caps an address at 254 characters, so
 * anything longer is not a real, deliverable address — and would otherwise let
 * a user paste thousands of characters into the field.
 */
export const MAX_EMAIL_LENGTH = 254;

/** Returns true when `email` is a well-formed, reasonably-sized email address. */
export const isValidEmail = (email: string): boolean => {
  const trimmed = email.trim();
  return trimmed.length <= MAX_EMAIL_LENGTH && EMAIL_REGEX.test(trimmed);
};

/**
 * Maximum allowed length for a person/business name field. Names longer than
 * this break table cells, cards and other fixed-width UI across the app, so we
 * cap them at the input and reject overflow during validation.
 */
export const MAX_NAME_LENGTH = 50;

/** Returns true when `name` is within the allowed name length. */
export const isValidNameLength = (name: string): boolean =>
  name.trim().length <= MAX_NAME_LENGTH;

/**
 * Maximum allowed length for the address line 2 / "Apt, suite, unit" field.
 * Real-world designators ("Apt 4B", "Building C, Suite 1200") fit comfortably;
 * the cap stops customers from pasting full sentences that later overflow or
 * get dropped from appointment displays (QA 86bakn3m0).
 */
export const MAX_ADDRESS_LINE2_LENGTH = 30;

/**
 * A valid person name contains only letters (Unicode-aware), apostrophes,
 * hyphens and spaces — no digits or other special characters. This mirrors the
 * rule enforced in the chatbot (`ConversationEngine.isValidName`) and the
 * in-chat contact form so every flow that collects a name rejects the same
 * invalid input (e.g. numeric characters).
 *
 * `\p{L}` matches any Unicode letter (accented, Cyrillic, CJK, etc.).
 */
const NAME_REGEX = /^[\p{L}' -]+$/u;

/** Returns true when `name` contains only letters, spaces, hyphens and apostrophes. */
export const isValidName = (name: string): boolean =>
  NAME_REGEX.test(name.trim());

/**
 * Validate a North American (NANP) phone number. The booking surfaces collect
 * and format US/Canada numbers, so beyond "has 10 digits" we enforce the real
 * NANP structural rules to reject obviously fake/unusable input such as
 * `0000000000`, `1111111111`, repeated digits, or `911`-style service codes:
 *
 *  - 10 digits (a leading country-code `1` is allowed and stripped)
 *  - area code (NPA) and exchange (NXX) must start with 2-9
 *  - not all the same digit
 *  - NPA is not an N11 service code (211, 311 … 911)
 */
/**
 * Lenient phone check for internal booking surfaces (Routy's in-chat form and
 * tools): exactly 10 digits (an optional leading `1` country code is
 * stripped) and not a single repeated digit. Unlike `isValidPhoneNumber`, it
 * does NOT enforce NANP area-code/exchange digit rules, so internal test data
 * like 921-111-3333 is accepted while a 17-digit paste is still rejected.
 */
export const isTenDigitPhone = (phone: string | null | undefined): boolean => {
  let digits = (phone || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  return digits.length === 10 && !/^(\d)\1{9}$/.test(digits);
};

/**
 * Clamp a phone field's raw input at its max digit count (10, or 11 when the
 * number starts with a `1` country code, matching `isTenDigitPhone`),
 * preserving any formatting characters typed before the cutoff. Lets a phone
 * input physically stop accepting digits past the limit instead of only
 * flagging the overflow afterwards (QA 86bavpvjp).
 */
export const clampPhoneInput = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  const maxDigits = digits.startsWith('1') ? 11 : 10;
  if (digits.length <= maxDigits) return value;
  let count = 0;
  let end = 0;
  for (let i = 0; i < value.length && count < maxDigits; i++) {
    if (/\d/.test(value[i])) count++;
    end = i + 1;
  }
  return value.slice(0, end);
};

export const isValidPhoneNumber = (phone: string | null | undefined): boolean => {
  let digits = (phone || '').replace(/\D/g, '');
  // Allow a leading US/Canada country code.
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  if (digits.length !== 10) return false;

  // Reject a number made of a single repeated digit (e.g. 2222222222).
  if (/^(\d)\1{9}$/.test(digits)) return false;

  const npa = digits.slice(0, 3);
  const nxx = digits.slice(3, 6);

  // NANP: area code and central-office code first digit must be 2-9.
  if (!/^[2-9]/.test(npa) || !/^[2-9]/.test(nxx)) return false;

  // N11 codes (211, 311, …, 911) are service codes, not valid area codes.
  if (/^[2-9]11$/.test(npa)) return false;

  return true;
};
